import { QRCodeSVG } from "qrcode.react";
import { useReducedMotion } from "framer-motion";
import { Printer, Smartphone, Layers, ShoppingCart, ScanLine, CheckCircle2 } from "lucide-react";
import { Reveal, SectionTag, SectionTitle, Bullet } from "./Reveal";

const LOTS = [
  { name: "Filé mignon porcionado", lot: "078A", left: "3 dias", pct: 68, tone: "bg-emerald-500" },
  { name: "Molho de tomate rústico", lot: "102C", left: "36 h", pct: 32, tone: "bg-amber-500" },
  { name: "Salmão fresco em postas", lot: "091B", left: "9 h", pct: 8, tone: "bg-rose-500" },
];

function ThermalLabel() {
  return (
    <div
      className="w-full max-w-[300px] aspect-[5/3] bg-white text-black border border-stone-300 shadow-[0_18px_40px_-20px_rgba(0,0,0,.45)] flex p-2 gap-2 select-none"
      style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
      data-testid="thermal-label-mockup"
    >
      <div className="flex items-center justify-center">
        <QRCodeSVG value="ULTRA-LOT-2026-078A" size={96} level="M" />
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-between leading-tight">
        <div>
          <div className="text-[11px] font-black uppercase truncate">Filé mignon porcionado 500 g</div>
          <div className="text-[9px] mt-0.5">Lote <b>078A</b> · Manip. 15/07 09:30</div>
          <div className="text-[9px]">Resp.: Marcos S.</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wide">Validade</div>
          <div className="text-[19px] font-black leading-none">18/07 · 12:00</div>
        </div>
        <div className="text-[8.5px] border-t border-black pt-0.5 flex justify-between">
          <span>Conservar 0 a 4 °C</span>
          <span className="font-bold">ULTRA</span>
        </div>
      </div>
    </div>
  );
}

export function PhoneScan({ reduce }) {
  return (
    <div className="w-[230px] rounded-[26px] bg-stone-950 border border-stone-800 p-2 shadow-2xl" data-testid="phone-scan-mockup">
      <div className="rounded-[20px] overflow-hidden bg-stone-900 relative aspect-[9/16]">
        <div className="absolute inset-0 bg-gradient-to-b from-stone-700 via-stone-800 to-stone-950" />
        <div className="absolute top-3 inset-x-0 text-center text-[10px] text-stone-300 font-body">Baixa de estoque · câmera</div>
        <div className="absolute left-1/2 top-[38%] -translate-x-1/2 -translate-y-1/2 scale-[0.42] origin-center opacity-90">
          <ThermalLabel />
        </div>
        <div className="absolute inset-x-6 top-[22%] bottom-[46%]">
          {["top-0 left-0 border-t-[3px] border-l-[3px]", "top-0 right-0 border-t-[3px] border-r-[3px]", "bottom-0 left-0 border-b-[3px] border-l-[3px]", "bottom-0 right-0 border-b-[3px] border-r-[3px]"].map((c) => (
            <span key={c} className={`absolute h-6 w-6 border-emerald-400 rounded ${c}`} />
          ))}
          {!reduce && <span className="absolute inset-x-1 h-0.5 bg-emerald-400/90 shadow-[0_0_10px_2px_rgba(16,185,129,.7)] phone-scanline" />}
        </div>
        <div className="absolute inset-x-3 bottom-3 rounded-xl bg-emerald-500 text-stone-950 px-3 py-2.5 shadow-lg">
          <div className="flex items-center gap-1.5 text-[11px] font-extrabold font-display">
            <CheckCircle2 className="h-4 w-4" /> QR detectado · baixa confirmada
          </div>
          <div className="text-[10px] font-body mt-0.5 text-emerald-950">1 un. · Lote 078A · Setor grelhados · 0,8 s</div>
        </div>
      </div>
      <style>{`
        @keyframes phone-scan { 0% { top: 0; } 50% { top: calc(100% - 2px); } 100% { top: 0; } }
        .phone-scanline { animation: phone-scan 2.6s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

export function StockMockup() {
  const reduce = useReducedMotion();
  return (
    <div className="relative" data-testid="stock-mockup">
      <div className="rounded-2xl border border-stone-200 bg-white shadow-xl p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500 font-body">Impressão de etiqueta</div>
            <div className="font-display font-bold text-stone-950">Térmica 50 × 30 mm · QR único</div>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-stone-700 bg-stone-100 border border-stone-200 rounded-full px-2.5 py-1 font-body">
            <Printer className="h-3 w-3" /> ZPL · ESC/POS · PDF A4
          </span>
        </div>
        <div className="grid grid-cols-[minmax(0,1fr)] sm:grid-cols-5 gap-6 items-start">
          <div className="sm:col-span-3 flex flex-col gap-5">
            <div className="rounded-xl bg-stone-100 border border-stone-200 p-4 flex justify-center">
              <ThermalLabel />
            </div>
            <div>
              <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-stone-500 font-body mb-2">
                <span>Fila PVPS · vence primeiro, sai primeiro</span>
                <Layers className="h-3.5 w-3.5" />
              </div>
              <ul className="space-y-2">
                {LOTS.map((l) => (
                  <li key={l.lot} className="rounded-lg border border-stone-200 px-3 py-2">
                    <div className="flex items-center justify-between text-xs font-body">
                      <span className="font-semibold text-stone-900 truncate">{l.name}</span>
                      <span className="font-mono text-stone-500 ml-2 whitespace-nowrap">{l.left}</span>
                    </div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-stone-200 overflow-hidden">
                      <div className={`h-full ${l.tone}`} style={{ width: `${l.pct}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="sm:col-span-2 flex justify-center sm:justify-end">
            <PhoneScan reduce={reduce} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PillarEstoque() {
  return (
    <section id="estoque" data-testid="landing-pillar-estoque" className="bg-stone-100 border-y border-stone-200 py-24 scroll-mt-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-[minmax(0,1fr)] lg:grid-cols-12 gap-12 lg:gap-10 items-center">
        <Reveal className="lg:col-span-5">
          <SectionTag>Pilar 03 · Controle de Estoque</SectionTag>
          <SectionTitle>Etiqueta impressa em 2 segundos. Baixa imediata com a câmera do celular.</SectionTitle>
          <p className="mt-5 font-body text-base md:text-lg text-stone-600 leading-relaxed">
            Elimine fita crepe e caneta que descolam no congelador. O Ultra padroniza etiquetas térmicas com QR Code e calcula a validade secundária automaticamente.
          </p>
          <ul className="mt-8 space-y-5">
            <Bullet icon={Printer} title="Impressão térmica pronta para uso" desc="Compatível com impressoras térmicas comuns, desde as mais simples até as mais robustas, você configura o tamanho da etiqueta." />
            <Bullet icon={Smartphone} title="Baixa escaneando com a câmera" desc="Abriu o pote? Aponte o celular ou tablet para a etiqueta e o lote é baixado em menos de 1 segundo." />
            <Bullet icon={ScanLine} title="Regra PVPS implacável" desc="O sistema prioriza os lotes que vencem primeiro e alerta em verde, âmbar e vermelho conforme a validade." />
            <Bullet icon={ShoppingCart} title="Sugestão de compras preditiva" desc="Cruza consumo real com estoque mínimo e gera o pedido exato — sem furo no fechamento do mês." />
          </ul>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-7">
          <StockMockup />
        </Reveal>
      </div>
    </section>
  );
}
